import constants from "lib/constants";
import moment from "moment/moment";
import ConfirmationModal from "ui/components/modals/confirmation/ConfirmationModal";
import { utilityFunctions } from "ui/utilities/utilityFunctions";

import {
  addIncludedFeedsToMarkets,
  cabinetMarketKey,
  collectIncludedMarketIdsFromSelections,
  normalizeConsistsOfPackageSelections,
  removeMarketSelection,
  removeMutuallyExclusiveSelections,
  buildMutualExclusivityGraph,
} from "./market-data-selection";

const { RequestFormStatusTypes, notActiveRequestsStates } = constants;

/**
 * UI label from one entry of `GET /static/marketdataproviders` → `bundle[]`.
 * Uses the first non-empty display-oriented field; falls back to `name` (used in `marketdataproviders[].bundle`).
 */
const getBundleDisplayLabelFromDoc = (bundleDoc) => {
  if (!bundleDoc || typeof bundleDoc !== "object") return "";
  const keys = ["label", "title", "displayName", "caption", "shortName"];
  for (let i = 0; i < keys.length; i += 1) {
    const v = bundleDoc[keys[i]];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return bundleDoc.name != null ? String(bundleDoc.name) : "";
};

/** Resolve UI label for a bundle key (`name`) using the `bundle` list from the same marketdata static payload. */
const resolveBundleDisplayLabel = (bundleName, marketBundles) => {
  if (bundleName == null || bundleName === "") return bundleName;
  const doc = marketBundles?.find((b) => b.name === bundleName);
  if (doc) {
    const label = getBundleDisplayLabelFromDoc(doc);
    if (label) return label;
  }
  return bundleName;
};

const getTPRefByName = (at, tp, tpt, tradingPlatforms) => {
  if (tradingPlatforms)
    return tradingPlatforms.find(
      (t) =>
        t.software === tp &&
        t.softwareType === tpt &&
        t.accountTypeRefs.includes(at)
    )?.reference;
  return "";
};

const findCurrentTradingPlatform = (profile, rates) => {
  if (rates) {
    const ratesValues = rates;
    const potentialTPS = ratesValues?.filter((tp) =>
      tp.accountTypeRefs.includes(profile.accountType)
    );

    let potentialPlatforms = profile.software
      ? potentialTPS.filter((tp) => tp.software === profile.software)
      : potentialTPS;
    if (potentialPlatforms.length > 1) {
      potentialPlatforms.forEach((platform) => {
        if (platform.conditionFields) {
          platform.conditionFields.forEach(({ conditions, fields }) => {
            const conditionKey = Object.keys(conditions)[0];
            const conditionValue = conditions[conditionKey];
            const profileValue = profile.agreement?.rules?.[conditionKey];
            if (
              conditionValue &&
              typeof conditionValue === "object" &&
              conditionValue.min !== undefined &&
              conditionValue.max !== undefined &&
              profileValue >= conditionValue.min &&
              profileValue <= conditionValue.max
            ) {
              if (fields.softwareType) {
                profile.softwareType = platform.softwareType;
              }
            }
          });
        }
      });
    }

    let foundPlatform = potentialPlatforms.find((tp) => {
      if (
        tp.softwareType === profile.softwareType &&
        tp.accountTypeRefs.includes(profile.accountType)
      ) {
        return true;
      }

      if (tp.conditionFields) {
        // if we have condition fields we search for a similarity
        const foundField = tp.conditionFields.find(
          (f) => f.fields.softwareType === profile.softwareType
        );
        if (foundField) {
          return true;
        }
      }

      if (
        !profile.software &&
        profile.accountType &&
        profile.accountType === "zimtraexpert" &&
        tp.accountTypeRefs.includes(profile.accountType)
      ) {
        return true;
      }
      return false;
    });

    return foundPlatform ? foundPlatform : null;
  }
};

const getBundleName = ({
  markets,
  marketdataProviders,
  currentAccount,
  tradingPlatforms,
  marketBundles,
}) => {
  const tradingPlatformRef = findCurrentTradingPlatform(
    currentAccount,
    tradingPlatforms
  )?.reference;
  // Filter market data entitlements based on the trading platform reference
  const relevantEntitlements = marketdataProviders?.filter((entitlement) =>
    entitlement.tradingPlatformRefs.includes(tradingPlatformRef)
  );

  const includedFeedsSet = collectIncludedMarketIdsFromSelections(
    markets || [],
    relevantEntitlements || [],
    cabinetMarketKey
  );
  
  // Filter out included feeds before matching bundle
  const marketsForMatching = markets?.filter((m) => !includedFeedsSet.has(m)) || [];

  // Further filter the entitlements to those whose names appear in the filtered markets list
  const matchedEntitlements = relevantEntitlements?.filter((entitlement) =>
    marketsForMatching?.includes(entitlement.name)
  );

  // Determine the bundle name based on the matched entitlements
  if (marketBundles) {
    for (const bundle of marketBundles) {
      if (matchedEntitlements?.length > 0) {
        const isAllMatchedEntitlementsInBundle = matchedEntitlements.every(
          (entitlement) => entitlement.bundle.includes(bundle.name)
        );

        const totalProvidersInBundle = relevantEntitlements.filter(
          (entitlement) => entitlement.bundle.includes(bundle.name)
        ).length;

        if (
          isAllMatchedEntitlementsInBundle &&
          totalProvidersInBundle === matchedEntitlements.length
        ) {
          return bundle.name; // All matched entitlements are in this bundle
        }
      }
    }
  }

  // If no bundle matches all conditions, return "Custom"
  return "Custom";
};

const getEffectiveDate = (requestedMarketsData) => {
  if (notActiveRequestsStates.includes(requestedMarketsData?.status)) {
    return moment().add(1, "M").startOf("month").format("YYYY-MM-DD");
  }
  return moment
    .utc(requestedMarketsData?.schedule.duedate)
    .format("YYYY-MM-DD");
};

// TODO: посмотри мой коммент для onMarketStatusChangeHandler. тут тоже самое
const setToPrimaryDataHandler = ({
  requestedMarketsData,
  setLocalState,
  localState,
  setPrimaryEntitlements,
  extensions,
  getMarkets,
}) => {
  const getMarketsFromExtHandler = (type) => {
    return getMarketsFromExt(type, requestedMarketsData, extensions);
  };
  const primeMarkets = getMarketsFromExtHandler("ext");
  let markets = getMarketsFromExtHandler("ext");
  const addMarkets = [];

  const removeMarkets = getMarketsFromExtHandler("remove");
  // set add markets
  if (
    requestedMarketsData.status !== RequestFormStatusTypes.COMPLETED &&
    requestedMarketsData.status !== RequestFormStatusTypes.DECLINED
  ) {
    getMarketsFromExtHandler("add")?.forEach((m) => {
      if (!getMarketsFromExtHandler("ext").includes(m)) {
        addMarkets.push(m);
      }
    });
  }
  // set localState markets
  if (!!removeMarkets?.length) {
    markets = markets.filter((m) => !removeMarkets.includes(m));
  }
  if (!!addMarkets?.length) {
    addMarkets.forEach((m) => !markets.includes(m) && markets.push(m));
  }

  if (getMarkets) {
    return markets;
  }

  setLocalState({
    ...localState,
    markets: markets,
    addMarkets,
    effectiveDate: getEffectiveDate(requestedMarketsData),
    removeMarkets: removeMarkets,
  });
  setPrimaryEntitlements(primeMarkets);
};

const getMarketsFromExt = (type, requestedMarketsData, extensions) => {
  if (type === "remove")
    return requestedMarketsData.ext?.removeMarkets?.map((m) => m.market);
  if (type === "add")
    return requestedMarketsData.ext?.addMarkets?.map((m) => m.market);
  if (type === "ext") {
    if (
      !!extensions?.length &&
      extensions?.find((e) => e.code === "trading/marketdata")
    ) {
      return extensions
        .find((e) => e.code === "trading/marketdata")
        ?.ext?.markets.map((m) => m.market);
    } else {
      return [];
    }
  }
  return requestedMarketsData.ext?.addMarkets?.map((m) => m.market);
};

// TODO: нужно переписать. helpers это функция которая помогает вам сделать что-то.
// Но в данном случае она делает что-то и сразу же меняет состояние. Это не правильно.
// нужно сделать функцию которая будет возвращать новое состояние, а потом уже в компоненте
// вызывать setLocalState и передавать туда новое состояние. тут не должно быть ничего что
// связано со изменениями какого либо состояния, рендерингом модалок и прочим
const onMarketStatusChangeHandler = ({
  name,
  value,
  reference,
  depRef,
  localState,
  setLocalState,
  filterMarketsToTradingPlatform,
  primaryEntitlements,
  setDataWasChanged,
  requestedMarketsData,
  blockRequest,
  setCurrentPopupAction,
  showModal,
  hideModal,
  setChosenButton,
}) => {
  const getMarketId = cabinetMarketKey;
  const findByRef = (ref) =>
    filterMarketsToTradingPlatform.find((m) => m.reference === ref);
  const graph = buildMutualExclusivityGraph(filterMarketsToTradingPlatform);

  let newMarkets = [...localState.markets];
  const newRemoveMarkets = [];
  const newAddMarkets = [];

  if (value) {
    if (!newMarkets.includes(name)) {
      if (reference) {
        removeMutuallyExclusiveSelections(
          newMarkets,
          reference,
          filterMarketsToTradingPlatform,
          getMarketId,
          findByRef,
          graph
        );
      }
      newMarkets.push(name);
      if (depRef?.length) {
        depRef.forEach((ref) => {
          const depMarket = findByRef(ref);
          if (depMarket && !newMarkets.includes(depMarket.name)) {
            newMarkets.push(depMarket.name);
          }
        });
      }
      const currentMarketItem = filterMarketsToTradingPlatform.find(
        (m) => m.name === name
      );
      if (currentMarketItem) {
        addIncludedFeedsToMarkets(
          newMarkets,
          currentMarketItem,
          findByRef,
          getMarketId
        );
      }
    }
  } else {
    removeMarketSelection(
      newMarkets,
      name,
      filterMarketsToTradingPlatform,
      getMarketId,
      findByRef
    );
  }

  normalizeConsistsOfPackageSelections(
    newMarkets,
    filterMarketsToTradingPlatform,
    getMarketId,
    findByRef
  );
  // compare markets chosen and primary
  const array2Sorted = newMarkets.slice().sort();
  if (
    primaryEntitlements.length === newMarkets.length &&
    primaryEntitlements
      .slice()
      .sort()
      .every((value, index) => value === array2Sorted[index]) &&
    !!newMarkets.length
  ) {
    if (notActiveRequestsStates.includes(requestedMarketsData.status)) {
      setDataWasChanged(() => false);
    }
  } else {
    setDataWasChanged(() => true);
    if (requestedMarketsData.status === RequestFormStatusTypes.OPEN) {
      blockRequest();
    }
  }
  // set addMarkets and removeMarkets
  filterMarketsToTradingPlatform.forEach((m) => {
    if (primaryEntitlements.includes(m.name) && !newMarkets?.includes(m.name))
      newRemoveMarkets.push(m.name);
    if (!primaryEntitlements?.includes(m.name) && newMarkets?.includes(m.name))
      newAddMarkets.push(m.name);
  });
  // detect no markets chosen
  if (newMarkets.length === 0) {
    showModal(
      <ConfirmationModal
        isApplyButtonRed
        showEditModal
        closeModal={hideModal}
        onApplyClick={() => setCurrentPopupAction("no-markets")}
        labels={{
          title: "Remove all Market Data subscriptions?",
          body: "Do you really want to unsubscribe from all Market Data Entitlements?",
          buttons: { apply: "Yes", decline: "No" },
        }}
      />
    );
  } else {
    setLocalState({
      ...localState,
      markets: newMarkets,
      addMarkets: newAddMarkets,
      removeMarkets: newRemoveMarkets,
    });
    setChosenButton(newMarkets);
  }
};

const getTotalPriceHandler = (markets, filterMarketsToTradingPlatform) => {
  if (markets) {
    const includedFeedsSet = collectIncludedMarketIdsFromSelections(
      markets,
      filterMarketsToTradingPlatform || [],
      cabinetMarketKey
    );
    
    const total = markets.reduce((acc, marketName) => {
      // Only count price if market is not included by another feed
      if (includedFeedsSet.has(marketName)) {
        return acc;
      }
      const market = filterMarketsToTradingPlatform?.find(
        (m) => m.name === marketName
      );
      return acc + (market?.price || 0);
    }, 0);

    return utilityFunctions.currencyFormatter().format(total);
  } else return utilityFunctions.currencyFormatter().format(0);
};

const setBundleColor = ({
  name,
  changingStatus,
  buttonItem,
  primaryBundle,
  requestedMarketsData,
}) => {
  if (name === buttonItem && buttonItem !== primaryBundle) {
    return "waiting-bundle";
  }
  if (
    name === buttonItem &&
    buttonItem === primaryBundle &&
    (changingStatus ||
      requestedMarketsData.status !== RequestFormStatusTypes.COMPLETED)
  ) {
    return "waiting-bundle";
  }
  if (name === primaryBundle) return "primary-bundle";
};

const setColorStatus = ({ name, primaryEntitlements, entitlements, isIncluded, isChecked }) => {
  // First check: newly added item (not in primary, but checked)
  if (!primaryEntitlements?.includes(name) && isChecked)
    return "added-item market-checkmark-done";
  
  // Second check: removed item (in primary, but not checked)
  if (primaryEntitlements?.includes(name) && !isChecked)
    return "removed-item market-checkmark";
  
  // Third check: checked item (checked, regardless of primary status or included status)
  if (isChecked)
    return "market-checkmark-done";
  
  // Not checked
  return "";
};

const getRequiredRefs = (arrayOfRefsToFind, marketdataProviders) => {
  return arrayOfRefsToFind
    ?.map((r) => marketdataProviders.find((m) => m.reference === r)?.name)
    .filter(Boolean); // Filter out any undefined values
};

const getMarketWithName = ({ filterMarketsToTradingPlatform, name }) => {
  return filterMarketsToTradingPlatform
    ?.filter((m) => m.bundle.includes(name))
    .map((m) => m.name);
};

const getMarketsToSend = ({ markets, filterMarketsToTradingPlatform }) => {
  const list = markets || [];
  const includedFeedsSet = collectIncludedMarketIdsFromSelections(
    list,
    filterMarketsToTradingPlatform || [],
    cabinetMarketKey
  );

  const marketsToSend = list.filter((m) => !includedFeedsSet.has(m));
  
  return (filterMarketsToTradingPlatform || [])
    .filter((f) => marketsToSend.includes(f.name))
    .map((f) => ({ group: f.group, market: f.name, reference: f.reference }));
};

export {
  getTPRefByName,
  getBundleName,
  getBundleDisplayLabelFromDoc,
  resolveBundleDisplayLabel,
  findCurrentTradingPlatform,
  setToPrimaryDataHandler,
  getMarketsFromExt,
  onMarketStatusChangeHandler,
  getTotalPriceHandler,
  setBundleColor,
  getRequiredRefs,
  getMarketWithName,
  setColorStatus,
  getMarketsToSend,
};
